import { Lightbulb, PartyPopper, Pencil } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Image, View, XStack, YStack } from "tamagui";
import { PlanQuestionType, PlanType } from "../../templates/types";
import { scrollToTop } from "../core/screens/types";
import { trackEvent } from "../core/trackEvent";
import type BaseModuleModel from "../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../modules/core/BaseModuleModel";
import { Body, BodyTextarea } from "../ui/Body";
import { Box } from "../ui/Box";
import { Button } from "../ui/Button";
import { Title, TitleTextarea } from "../ui/Title";
import type PlanModel from "./PlanModel";
import { usePlanNavigation } from "./usePlanNavigation";

export const PlanQuickstartAddStep = observer(
  ({
    module,
  }: {
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>;
  }) => {
    const { t } = useTranslation();
    const { navigate } = usePlanNavigation();

    const plan = module.plan;

    if (plan.data.type !== PlanType.Quickstart || !module.isDone) {
      return null;
    }

    let content = null;

    // If it has a special flow to follow, present that instead
    if (module.data.completeAction) {
      const completeData = module.data.completeAction;
      if (completeData) {
        const ctaAction = async () => {
          let firstModuleCreated = "";
          for (const moduleData of completeData.addModules) {
            const moduleCreated = await module.section.addModule({
              ...moduleData,
              completeAction: moduleData.completeAction ?? null,
              questionType:
                moduleData.questionOption?.questionType ??
                PlanQuestionType.None,
              initiated: true,
              isDraggable: moduleData.isDraggable ?? true,
            });

            if (!firstModuleCreated) {
              firstModuleCreated = moduleCreated.uid;
            }
          }

          // set to first one created
          if (firstModuleCreated) {
            module.resetCompletionType();

            // Navigate to it at once
            navigate("Module", {
              planUid: plan.uid,
              moduleUid: firstModuleCreated,
            });
            scrollToTop();
            trackEvent("flow_continue_accept", { type: module.data.type });
          }
        };

        content = (
          <YStack gap="$medium" flex={1} marginLeft={-24}>
            <View
              borderTopColor={"$divider"}
              borderTopWidth={1}
              marginTop={"$medium"}
              paddingTop={"$medium"}
            >
              <YStack gap={"$medium"}>
                <XStack gap="$space.small" alignItems="center">
                  <Lightbulb minWidth={"$medium"} />
                  <Title>{completeData.question}</Title>
                </XStack>
                <Body>{completeData.description}</Body>
              </YStack>
            </View>
            <XStack gap="$small">
              <Button
                onPress={() => {
                  module.resetCompletionType();
                  trackEvent("flow_continue_deny", { type: module.data.type });
                }}
                type="secondary"
              >
                {t("plan_add_step_cta_deny")}
              </Button>
              <Button flex={1} icon={PartyPopper} onPress={ctaAction}>
                {t("plan_add_step_cta_accept")}
              </Button>
            </XStack>
          </YStack>
        );
      }
    }

    return content;
  },
);

export const PlanQuickstartOutroInfo = observer(
  ({
    title,
    description,
    editOptions,
  }: {
    title: string | undefined;
    description: string | undefined;
    editOptions?: {
      onTitleEdit: (text: string) => void;
      onDescriptionEdit: (text: string) => void;
    };
  }) => {
    return (
      <YStack gap="$medium">
        <Box position="relative" backgroundColor={"$primary"}>
          <XStack gap="$space.small" alignItems="center">
            <PartyPopper />
            {!editOptions && <Title>{title}</Title>}
            {!!editOptions && (
              <TitleTextarea
                icon={Pencil}
                focusOnIconPress
                defaultValue={title ?? ""}
                submitOnBlur
                submitOnEnter
                blurOnSubmit
                onSubmitEditing={(e) => {
                  editOptions.onTitleEdit(e.nativeEvent.text);
                }}
              />
            )}
          </XStack>
          <YStack gap={"$medium"}>
            {!editOptions && <Body>{description}</Body>}
            {!!editOptions && (
              <BodyTextarea
                icon={Pencil}
                focusOnIconPress
                defaultValue={description ?? ""}
                submitOnBlur
                submitOnEnter
                blurOnSubmit
                onSubmitEditing={(e) => {
                  editOptions.onDescriptionEdit(e.nativeEvent.text);
                }}
              />
            )}
          </YStack>
        </Box>
      </YStack>
    );
  },
);

export const PlanQuickstartAddGeneralStep = observer(
  ({ plan }: { plan: PlanModel }) => {
    const { t } = useTranslation();
    return (
      <View>
        <Box position="relative">
          <XStack gap="$space.small" alignItems="center">
            <Lightbulb />
            <Title>{t("plan_add_step_general_question")}</Title>
          </XStack>
          <YStack
            gap={"$medium"}
            $xs={{
              paddingRight: 134,
            }}
            paddingRight={163}
          >
            <Body>{t("plan_add_step_general_description")}</Body>
            <Image
              $xs={{
                paddingRight: -40,
              }}
              right={-12}
              position="absolute"
              bottom={-29}
              zIndex={1}
              src={require("../../assets/illustrations/laptopgirl.png")}
            />
          </YStack>
        </Box>
        <YStack gap="$medium">
          <Button
            type="secondary"
            icon={PartyPopper}
            onPress={() => plan.setShowAddStepSheet(true)}
          >
            {t("plan_add_step_general_accept")}
          </Button>
        </YStack>
      </View>
    );
  },
);
